/**
 * **흰 배경을 지워 자연스럽게 놓이게 한다** — 판정과 제거를 **한 곳에** 둔다.
 *
 * ## 왜 필요한가
 * 캐릭터·소품은 배경이 투명해야 마을에 얹힌다. 배경이 남아 있으면 **흰 네모가 뜬다.**
 * 그런데 AI(GPT·제미나이)가 만든 그림은 **흰 배경**으로 나온다 — 지금까지는 개발자가
 * 매번 스크립트를 돌려 지워 왔고, **운영자가 직접 올리는 길에는 아무 장치가 없다.**
 *
 * 실제로 `validateAsset` 은 JPG 만 막고, **PNG 는 검사조차 안 했다**(가장 흔한 형식인데).
 * 운영자가 GPT 로 만든 PNG 를 올리면 경고 하나 없이 게시되고 화면에 흰 네모가 나온다.
 *
 * ## 왜 여기(shared)에 두나
 * 관리자 미리보기(브라우저 canvas)와 개발 스크립트(sharp)가 **같은 판정**을 써야 한다.
 * 화면마다 다시 짜면 "관리자에선 지워졌는데 화면엔 흰 네모"가 된다(CLAUDE.md 규칙).
 * 그래서 이 파일은 **픽셀 배열만 받는 순수 함수**다 — canvas 도 sharp 도 아닌 곳에 둔다.
 *
 * ## 어떻게 지우나 — 가장자리에서 흘려보낸다(flood fill)
 * "밝은 픽셀을 전부 지우기" 로 하면 **옷의 흰 부분·병아리 알껍데기까지 뚫린다.**
 * 그래서 **가장자리에서 시작해 이어진 부분만** 지운다. 안쪽에 갇힌 흰색은 남는다.
 * (`scripts/fit-to-existing.mjs` 가 쓰던 방식과 같은 판단이다.)
 */

/** RGBA 픽셀 한 장. canvas 의 ImageData 와 sharp 의 raw 버퍼가 둘 다 이 모양이다. */
export interface Pixels {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export interface BackgroundReport {
  /** 가장자리가 불투명하다 = 배경이 안 지워진 그림이다. */
  hasBackground: boolean;
  /** 가장자리 픽셀 중 불투명한 비율(0~1). */
  edgeOpaque: number;
  /** 가장자리의 대표 색 `#rrggbb` — 흰색이면 AI 생성물일 가능성이 높다. */
  edgeColor: string;
  /** 그 대표 색이 흰색에 가까운가. 자동 제거를 권할지 판단한다. */
  edgeIsWhite: boolean;
}

const hex = (r: number, g: number, b: number) =>
  "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");

/**
 * 배경이 남아 있는지 본다. **가장자리만** 훑으므로 큰 그림에서도 빠르다.
 *
 * 가장자리를 보는 이유: 캐릭터·소품은 가운데 있고 가장자리는 배경이다.
 * 가장자리가 비어 있으면(투명) 이미 지워진 그림이다.
 */
export function inspectBackground(px: Pixels): BackgroundReport {
  const { data, width: w, height: h } = px;
  if (w < 2 || h < 2) {
    return { hasBackground: false, edgeOpaque: 0, edgeColor: "#000000", edgeIsWhite: false };
  }
  let opaque = 0;
  let total = 0;
  let r = 0, g = 0, b = 0;
  const at = (x: number, y: number) => (y * w + x) * 4;
  const take = (i: number) => {
    total++;
    if (data[i + 3]! > 8) {
      opaque++;
      r += data[i]!; g += data[i + 1]!; b += data[i + 2]!;
    }
  };
  for (let x = 0; x < w; x++) { take(at(x, 0)); take(at(x, h - 1)); }
  for (let y = 1; y < h - 1; y++) { take(at(0, y)); take(at(w - 1, y)); }

  const edgeOpaque = total === 0 ? 0 : opaque / total;
  const [ar, ag, ab] = opaque === 0 ? [0, 0, 0] : [r / opaque, g / opaque, b / opaque];
  // 가장자리가 절반 넘게 차 있으면 배경이 남은 것으로 본다.
  // (그림이 프레임을 꽉 채우는 경우도 있어 "조금 걸친 것"은 배경으로 안 본다)
  return {
    hasBackground: edgeOpaque > 0.5,
    edgeOpaque,
    edgeColor: hex(ar, ag, ab),
    edgeIsWhite: opaque > 0 && ar > 225 && ag > 225 && ab > 225,
  };
}

export interface CutoutOptions {
  /**
   * 배경으로 볼 색 차이(0~255). 크게 잡을수록 많이 지운다.
   * 기본 26 — 흰 배경의 옅은 그림자까지는 지우고, 크림색 소품은 남기는 값.
   */
  tolerance?: number;
  /** 가장자리 색을 배경색으로 쓴다. 지정하면 그 색을 배경으로 본다. */
  background?: { r: number; g: number; b: number };
  /**
   * 경계를 한 겹 부드럽게 만든다(기본 true).
   * 안 하면 잘린 자리가 **톱니처럼** 보인다 — 48px 로 줄이면 특히 티가 난다.
   */
  feather?: boolean;
}

export interface CutoutResult {
  /** 지운 픽셀 수. 0 이면 배경을 못 찾은 것이다. */
  removed: number;
  /** 지운 뒤 남은 그림의 사각형. 잘라내기(trim)에 쓴다. */
  bounds: { left: number; top: number; right: number; bottom: number } | null;
}

/**
 * 가장자리에서 이어진 배경을 지운다(**제자리에서** 픽셀을 고친다).
 *
 * 안쪽에 갇힌 흰색(옷·알껍데기)은 **남는다** — 그게 이 방식을 쓰는 이유다.
 */
export function cutoutBackground(px: Pixels, opt: CutoutOptions = {}): CutoutResult {
  const { data, width: w, height: h } = px;
  const tol = opt.tolerance ?? 26;
  const feather = opt.feather ?? true;
  if (w < 2 || h < 2) return { removed: 0, bounds: null };

  const bg = opt.background ?? edgeAverage(px);
  const seen = new Uint8Array(w * h);
  // 가장자리 전체를 시작점으로 넣는다 — 어느 쪽이 배경인지 모르기 때문이다
  const stack: number[] = [];
  for (let x = 0; x < w; x++) { stack.push(x, 0, x, h - 1); }
  for (let y = 0; y < h; y++) { stack.push(0, y, w - 1, y); }

  let removed = 0;
  const near = (i: number) =>
    Math.abs(data[i]! - bg.r) <= tol &&
    Math.abs(data[i + 1]! - bg.g) <= tol &&
    Math.abs(data[i + 2]! - bg.b) <= tol;

  while (stack.length) {
    const y = stack.pop()!;
    const x = stack.pop()!;
    if (x < 0 || y < 0 || x >= w || y >= h) continue;
    const p = y * w + x;
    if (seen[p]) continue;
    seen[p] = 1;
    const i = p * 4;
    if (data[i + 3] === 0) { pushNeighbors(stack, x, y); continue; } // 이미 투명 — 계속 번진다
    if (!near(i)) continue; // 그림을 만났다 — 여기서 멈춘다
    data[i + 3] = 0;
    removed++;
    pushNeighbors(stack, x, y);
  }

  // 지운 게 없으면 **아무것도 건드리지 않는다.**
  // 안 그러면 두 번 돌릴 때마다 경계가 한 겹씩 더 투명해져 그림이 야금야금 깎인다
  // (운영자가 "한 번 더 눌러 볼까" 하는 순간 벌어진다).
  if (feather && removed > 0) featherEdges(px);
  return { removed, bounds: opaqueBounds(px) };
}

function pushNeighbors(stack: number[], x: number, y: number) {
  stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
}

/** 가장자리 불투명 픽셀의 평균색 = 배경색으로 본다. */
function edgeAverage(px: Pixels): { r: number; g: number; b: number } {
  const { data, width: w, height: h } = px;
  let r = 0, g = 0, b = 0, n = 0;
  const take = (i: number) => {
    if (data[i + 3]! > 8) { r += data[i]!; g += data[i + 1]!; b += data[i + 2]!; n++; }
  };
  const at = (x: number, y: number) => (y * w + x) * 4;
  for (let x = 0; x < w; x++) { take(at(x, 0)); take(at(x, h - 1)); }
  for (let y = 1; y < h - 1; y++) { take(at(0, y)); take(at(w - 1, y)); }
  return n === 0 ? { r: 255, g: 255, b: 255 } : { r: r / n, g: g / n, b: b / n };
}

/**
 * 투명한 이웃이 있는 불투명 픽셀의 알파를 낮춰 경계를 한 겹 부드럽게 한다.
 * 48px 로 줄일 때 톱니가 눈에 띄는 것을 줄인다.
 */
function featherEdges(px: Pixels) {
  const { data, width: w, height: h } = px;
  const soften: number[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (data[i + 3] === 0) continue;
      const nb = [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const;
      for (const [nx, ny] of nb) {
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        if (data[(ny * w + nx) * 4 + 3] === 0) { soften.push(i); break; }
      }
    }
  }
  for (const i of soften) data[i + 3] = Math.round(data[i + 3]! * 0.55);
}

/** 남은 그림의 사각형. 없으면 null(전부 지워진 것 — 뭔가 잘못된 경우다). */
export function opaqueBounds(px: Pixels): CutoutResult["bounds"] {
  const { data, width: w, height: h } = px;
  let left = w, top = h, right = -1, bottom = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3]! > 8) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }
  return right < 0 ? null : { left, top, right, bottom };
}
